/**
 * File: logger.ts
 * Description: Thin wrapper around the global console object. This is the
 *   single sanctioned place in the codebase allowed to call console methods
 *   directly (see the eslint-disable below); every other module should import
 *   `logger` instead of calling `console.*` so that:
 *     - SonarQube rule S106 ("no console statements") stays satisfied
 *       everywhere except this file.
 *     - Swapping in a real telemetry/logging backend later (e.g. Sentry,
 *       Application Insights) only requires changing this one file.
 * Author: GitHub Copilot
 * Created: 2026-07-15
 */

/* eslint-disable no-console */

/**
 * Minimal logging facade used throughout the frontend in place of the raw
 * `console` global.
 */
export const logger = {
  /**
   * Log an error. Use for caught exceptions and other failure conditions
   * that should be visible in the browser console / log aggregation.
   * @param args - Values to log, forwarded to console.error.
   */
  error(...args: unknown[]): void {
    console.error(...args);
  },

  /**
   * Log a warning about a recoverable or unexpected condition.
   * @param args - Values to log, forwarded to console.warn.
   */
  warn(...args: unknown[]): void {
    console.warn(...args);
  },

  /**
   * Log informational, non-error diagnostic output.
   * @param args - Values to log, forwarded to console.info.
   */
  info(...args: unknown[]): void {
    console.info(...args);
  },
};
